import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useRef } from "react"
import { channelService } from "@/services/channel.service"

type ChannelParamsSyncOptions = {
  enabled?: boolean
  debounceMs?: number
  onSuccess?: (updatedChannels: string[]) => void
  onError?: (error: string) => void
  disciplineCode?: string
}

/**
 * Hook para sincronizar parámetros (como fechas) con channels del scoreboard
 * del usuario actual
 */
export function useChannelParamsSync(options: ChannelParamsSyncOptions = {}) {
  const { enabled = true, debounceMs = 500, onSuccess, onError } = options
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const lastParamsRef = useRef<Record<string, unknown>>({})

  /**
   * Función principal para sincronizar parámetros con channels específicos
   * @param params - Parámetros a sincronizar (ej: {date: "2024-01-15", venue: "Court A"})
   * @param channelIds - Array de IDs de channels a actualizar
   */
  const syncParams = useCallback(
    async (params: Record<string, unknown>, channelIds: string[]) => {
      if (!enabled || channelIds.length === 0)
        return

      // Avoid unnecessary syncs with the same parameters
      const paramsString = JSON.stringify({ params, channelIds })
      const lastParamsString = JSON.stringify(lastParamsRef.current)
      if (paramsString === lastParamsString)
        return

      lastParamsRef.current = { params, channelIds }

      try {
        // Clear previous debounce
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }

        // Debounce the API call
        // eslint-disable-next-line ts/no-misused-promises
        debounceRef.current = setTimeout(async () => {
          try {
            const result = await channelService.updateChannelParams(params, channelIds)

            if (result.success) {
              onSuccess?.(result.updatedChannels)
            }
            else {
              onError?.("Failed to sync parameters")
            }
          }
          catch (error) {
            console.error("Error syncing channel params:", error)
            onError?.(error instanceof Error ? error.message : "Unknown error")
          }
        }, debounceMs)
      }
      catch (error) {
        console.error("Error setting up channel params sync:", error)
        onError?.(error instanceof Error ? error.message : "Unknown error")
      }
    },
    [enabled, debounceMs, onSuccess, onError],
  )

  // Obtiene todos los channels del usuario actual
  const getUserChannels = useCallback(async () => {
    try {
      const channels = await channelService.getChannelsByUser()
      return channels
    }
    catch (error) {
      console.error("Error getting user channels:", error)
      onError?.(error instanceof Error ? error.message : "Unknown error")
      return []
    }
  }, [onError])

  const syncParamsWithUserChannels = useCallback(
    async (params: Record<string, unknown>) => {
      const userChannels = await getUserChannels()
      const channelIds = userChannels.map(channel => channel.id)

      if (channelIds.length === 0) {
        console.warn("No user channels available for sync")
        return
      }

      await syncParams(params, channelIds)
    },
    [syncParams, getUserChannels],
  )

  /**
   * Sincroniza parámetros con channels específicos
   */
  const useUserChannels = () => {
    return useQuery({
      queryKey: ["channels"],
      queryFn: async () => channelService.getChannelsByUser(),
      enabled,
    })
  }

  /**
   * Sincroniza parámetros de fecha con channels específicos o todos del usuario
   * @param date - Fecha en formato YYYY-MM-DD o null para limpiar
   * @param channelIds - (Opcional) Array de channel IDs específicos. Si no se proporciona, usa todos los del usuario
   */
  const syncDateParams = useCallback(
    async (date: string | null, channelIds?: string[]) => {
      const params: Record<string, unknown> = {}

      if (date) {
        params.date = date
      }

      if (channelIds) {
        await syncParams(params, channelIds)
      }
      else {
        await syncParamsWithUserChannels(params)
      }
    },
    [syncParams, syncParamsWithUserChannels],
  )

  // Cleanup debounce on unmounted
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return {
    syncParams,
    syncParamsWithUserChannels,
    getUserChannels,
    syncDateParams,
    useUserChannels,
  }
}
